import { addMonths, type TenantSettings } from '@dairy/shared';
import { eventsOf, latestOnOrBefore } from './events';
import type { DomainAnimal, DomainEvent } from './types';

export const STAGES = ['CALF', 'HEIFER', 'MILKING', 'DRY', 'BULL'] as const;
export type LifeStage = (typeof STAGES)[number];
export type Stage = LifeStage | 'SOLD' | 'DEAD' | 'CULLED';

/** Is the animal still younger than the calf age on `date`? (calendar months) */
export function isCalfAge(birthDate: string, date: string, s: Pick<TenantSettings, 'calfMaxAgeMonths'>): boolean {
  return date < addMonths(birthDate, s.calfMaxAgeMonths);
}

/** D-1 Stage on a date. `events` may contain other animals' events; they are ignored. */
export function deriveStage(
  animal: DomainAnimal,
  events: readonly DomainEvent[],
  date: string,
  s: Pick<TenantSettings, 'calfMaxAgeMonths'>,
): Stage {
  if (animal.status !== 'ACTIVE' && animal.exitDate && date > animal.exitDate) return animal.status;
  const young = isCalfAge(animal.birthDate, date, s);
  if (animal.sex === 'MALE') return young ? 'CALF' : 'BULL';
  const evs = eventsOf(events, animal.id);
  const calving = latestOnOrBefore(evs, 'CALVING', date);
  if (!calving) return young ? 'CALF' : 'HEIFER';
  const dry = evs.some(
    (e) => e.type === 'DRY_OFF' && e.date >= calving.date && e.date < date,
  );
  return dry ? 'DRY' : 'MILKING';
}

/** Was the animal on the farm on `date`? (arrived by then and had not exited before it) */
export function isOnFarm(animal: DomainAnimal, date: string): boolean {
  if (animal.deletedAt) return false;
  const arrived = animal.purchaseDate && animal.purchaseDate > animal.birthDate ? animal.purchaseDate : animal.birthDate;
  if (date < arrived) return false;
  if (animal.status !== 'ACTIVE' && animal.exitDate && date > animal.exitDate) return false;
  return true;
}

/** Was the animal active (not exited) on `date`? Events and milk records after the exit date are refused (AR-4). */
export function isActiveOn(animal: DomainAnimal, date: string): boolean {
  return !(animal.status !== 'ACTIVE' && animal.exitDate && date > animal.exitDate);
}
